import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { bringIntoBookApi } from '../api/bringIntoBook';
import { useSyncStoryContext } from '../hooks/useSyncStoryContext';
import StoryPicker from '../components/Common/StoryPicker';
import { remainingOffers } from '../utils/bringIntoBook';
import { bringIntoBookPath } from '../utils/bringIntoBookPath';
import {
  ALL_DONE,
  BRING_LEDE,
  BRING_TITLE,
  DEST_NOTES,
  EMPTY_LIST,
  KIND_LABEL,
  STATE_ACCEPTED,
  STATE_SKIPPED,
} from './bringIntoBookCopy';
import { ACCEPT_NOTE_ERROR, LOAD_SERIES_NOTES_ERROR } from '../utils/writerCopy';
import '../components/BookSheet/BookSheet.css';
import '../pages/SeriesOffersPage.css';
import './BringIntoBookPage.css';

/**
 * Bring into this book (#900). Standing series snapshots. Propose, accept,
 * skip. Not a left-nav room. Does not generate Plot Dossier, Logline, or PlotTrack.
 */
export default function BringIntoBookPage() {
  const { storyId: routeStoryId } = useParams();
  const { currentStory, setProjectAndStory } = useApp();
  const navigate = useNavigate();

  const openedId = routeStoryId || currentStory?.id || null;
  const { storyId, loadedStory } = useSyncStoryContext({ routeStoryId: openedId });

  const [offers, setOffers] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [done, setDone] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const storyName = loadedStory?.name || currentStory?.name || '';

  useEffect(() => {
    if (routeStoryId) return;
    if (currentStory?.id) {
      navigate(bringIntoBookPath(currentStory.id), { replace: true });
    }
  }, [routeStoryId, currentStory?.id, navigate]);

  const loadOffers = useCallback(async () => {
    if (!storyId) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await bringIntoBookApi.list(storyId);
      const rows = payload?.offers || [];
      setOffers(rows);
      const nextDrafts = {};
      rows.forEach((row) => {
        nextDrafts[row.id] = { name: row.name || '', statement: row.statement || '' };
      });
      setDrafts(nextDrafts);
    } catch (err) {
      setError(err.message || LOAD_SERIES_NOTES_ERROR);
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, [storyId]);

  useEffect(() => { loadOffers(); }, [loadOffers]);

  const remaining = remainingOffers(offers, done);

  const editDraft = (id, field, value) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const accept = async (row) => {
    const draft = drafts[row.id] || row;
    setSavingId(row.id);
    setError(null);
    try {
      await bringIntoBookApi.accept(storyId, {
        series_snapshot_id: row.id,
        kind: row.kind,
        name: draft.name,
        statement: draft.statement,
      });
      setDone((prev) => ({ ...prev, [row.id]: 'accepted' }));
    } catch (err) {
      setError(err.message || ACCEPT_NOTE_ERROR);
    } finally {
      setSavingId(null);
    }
  };

  const skip = async (row) => {
    setSavingId(row.id);
    try {
      await bringIntoBookApi.skip(storyId, { series_snapshot_id: row.id });
    } catch (_err) {
      // Skip is client-only if the call fails.
    }
    setDone((prev) => ({ ...prev, [row.id]: 'skipped' }));
    setSavingId(null);
  };

  const handlePick = useCallback((project, story) => {
    setProjectAndStory(project, story);
    setPickerOpen(false);
    navigate(bringIntoBookPath(story.id));
  }, [setProjectAndStory, navigate]);

  if (!storyId) {
    return (
      <div className="bs-panel">
        <button type="button" className="bs-btn bs-btn--primary" onClick={() => setPickerOpen(true)}>
          Choose a story
        </button>
        <StoryPicker
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onPick={handlePick}
          currentStoryId={storyId}
          title="Open Bring into this book"
        />
      </div>
    );
  }

  if (loading) return <div className="bs-panel"><p>Loading…</p></div>;

  return (
    <div className="bs-panel bib-page">
      <div className="bs-header">
        <div>
          <h1>{BRING_TITLE}</h1>
          <p className="bs-header__subtitle">{storyName}</p>
        </div>
        <Link className="bs-add-link" to="/bibles">Open Book Notes</Link>
      </div>

      <p className="so-lede">{BRING_LEDE}</p>

      {error && (
        <div className="bs-error">
          {error}{' '}
          <button type="button" className="bs-btn--danger" onClick={() => setError(null)}>dismiss</button>
        </div>
      )}

      {offers.length === 0 && !error && (
        <p className="so-lede">{EMPTY_LIST}</p>
      )}

      {offers.length > 0 && remaining.length === 0 && (
        <p className="so-lede">
          {ALL_DONE}
          {' '}
          <Link to="/bibles">Open Book Notes</Link>
        </p>
      )}

      <ul className="so-cards">
        {offers.map((row) => {
          const status = done[row.id];
          if (status) {
            return (
              <li key={row.id} className="so-card so-card--done">
                <p>
                  {status === 'accepted' ? STATE_ACCEPTED : STATE_SKIPPED}
                  {' '}
                  {row.name}
                  {status === 'accepted' && (
                    <>
                      {' '}
                      <Link to="/bibles">Open Book Notes</Link>
                    </>
                  )}
                </p>
              </li>
            );
          }
          const draft = drafts[row.id] || { name: row.name, statement: row.statement };
          return (
            <li key={row.id} className="so-card">
              <p className="bib-kind">{KIND_LABEL[row.kind] || row.kind}</p>
              <label className="so-field">
                <span>Name</span>
                <input
                  value={draft.name}
                  onChange={(e) => editDraft(row.id, 'name', e.target.value)}
                />
              </label>
              <label className="so-field">
                <span>Snapshot</span>
                <textarea
                  rows={4}
                  value={draft.statement}
                  onChange={(e) => editDraft(row.id, 'statement', e.target.value)}
                />
              </label>
              <p className="so-dest">{DEST_NOTES[row.kind]}</p>
              <div className="so-actions">
                <button
                  type="button"
                  className="bs-btn bs-btn--primary bs-btn--small"
                  onClick={() => accept(row)}
                  disabled={savingId === row.id}
                >
                  Accept
                </button>
                <button
                  type="button"
                  className="bs-btn bs-btn--secondary bs-btn--small"
                  onClick={() => skip(row)}
                  disabled={savingId === row.id}
                >
                  Skip
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
